import { FC } from 'react';

import { AdminCategory, AdminItem, BundleSource, Tag, resolveImageSrc } from '../../../api/client';
import { Button, Field, IconButton, Input, Meta, Select } from '../../../ui';
import { GroupDraft, GroupStats, plural } from './types';

type BundleRequirementGroupProps = {
  group: GroupDraft;
  index: number;
  totalGroups: number;
  stat: GroupStats;
  problem: string | null;
  categories: AdminCategory[];
  tags: Tag[];
  itemById: Map<number, AdminItem>;
  onChange: (key: string, patch: Partial<GroupDraft>) => void;
  onRemove: (key: string) => void;
  onOpenPicker: (group: GroupDraft) => void;
  onViewAll: (title: string, items: AdminItem[]) => void;
};

/**
 * One "choose N from ..." requirement group.
 *
 * The sentence ("Choose [N] from [source]") is composed here because it is the
 * bundle's grammar, not a shared layout. Every change is reported up through
 * `onChange`; the editor owns the draft.
 */
const BundleRequirementGroup: FC<BundleRequirementGroupProps> = ({
  group,
  index,
  totalGroups,
  stat,
  problem,
  categories,
  tags,
  itemById,
  onChange,
  onRemove,
  onOpenPicker,
  onViewAll,
}) => {
  const source = group.source;
  const tagName = tags.find((tag) => tag.id === group.tagId)?.name ?? 'a tag';
  const eligibleTitle = source === 'tag' ? tagName : (categories.find((category) => category.id === group.categoryId)?.name ?? 'Category');

  const eligiblePreview = (
    <div className="discountGroupEligible">
      <div className="discountGroupThumbs" aria-hidden="true">
        {stat.eligible
          .slice(0, 4)
          .map((item) =>
            item.image_url || item.img ? (
              <img className="discountGroupThumb" key={item.id} src={resolveImageSrc(item.image_url || item.img)} alt="" />
            ) : (
              <span className="discountGroupThumb discountGroupThumbEmpty" key={item.id} />
            )
          )}
      </div>
      <span className="discountGroupCount">{plural(stat.count, 'eligible item')}</span>
      {stat.count > 0 && (
        <button type="button" className="discountViewAll" onClick={() => onViewAll(eligibleTitle, stat.eligible)}>
          View all
        </button>
      )}
    </div>
  );

  return (
    <div className="discountGroup" role="group" aria-label={'Requirement group ' + (index + 1)} data-group-index={index}>
      <div className="discountGroupSentence">
        <span className="discountGroupWord">Choose</span>
        <Input
          className="discountGroupQty"
          type="number"
          min={1}
          value={group.quantity}
          aria-label={'Quantity for group ' + (index + 1)}
          onChange={(event) => onChange(group.key, { quantity: Math.max(1, Number(event.target.value) || 1) })}
        />
        <span className="discountGroupWord">from</span>
        <Select
          className="discountGroupSource"
          value={source}
          aria-label={'Source for group ' + (index + 1)}
          onChange={(event) => onChange(group.key, { source: event.target.value as BundleSource })}
        >
          <option value="category">Category</option>
          <option value="items">Selected items</option>
          {source === 'tag' && <option value="tag">{tagName}</option>}
        </Select>
        {totalGroups > 1 && (
          <IconButton className="discountGroupRemove" label={'Remove group ' + (index + 1)} onClick={() => onRemove(group.key)}>
            ×
          </IconButton>
        )}
      </div>

      {source === 'category' && (
        <div className="discountGroupBody">
          <Field label="Category">
            <Select
              className="discountGroupCategory"
              value={group.categoryId}
              aria-label={'Category for group ' + (index + 1)}
              onChange={(event) => onChange(group.key, { categoryId: Number(event.target.value) })}
            >
              <option value={0}>Choose a category…</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
          </Field>
          {eligiblePreview}
        </div>
      )}

      {source === 'tag' && (
        <div className="discountGroupBody">
          <Meta>Items tagged “{tagName}”.</Meta>
          {eligiblePreview}
        </div>
      )}

      {source === 'items' && (
        <div className="discountGroupBody">
          {group.itemIds.length === 0 ? (
            <Meta>No items selected yet.</Meta>
          ) : (
            <div className="discountChips">
              {group.itemIds.map((id) => (
                <span className="discountChip" key={id}>
                  {itemById.get(id)?.title ?? 'Item ' + id}
                  <button
                    type="button"
                    className="discountChipRemove"
                    aria-label={'Remove ' + (itemById.get(id)?.title ?? 'item') + ' from group ' + (index + 1)}
                    onClick={() => onChange(group.key, { itemIds: group.itemIds.filter((entry) => entry !== id) })}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="discountGroupEligible">
            <Button variant="secondary" onClick={() => onOpenPicker(group)}>
              Select items
            </Button>
            <span className="discountGroupCount">{plural(stat.count, 'eligible item')}</span>
          </div>
        </div>
      )}

      {problem && <p className="ui-field-error">{problem}</p>}
    </div>
  );
};

export default BundleRequirementGroup;
